import { supabase } from '@/services/supabase/client';
import env from '@/utils/env';

export type SearchEntityType = 'knowledge' | 'industry' | 'goal' | 'workflow' | 'product' | 'problem';

export interface SearchFilters {
  industry_id?: string;
  goal_id?: string;
  difficulty?: string;
  entity_type?: SearchEntityType;
}

export interface SearchContext {
  current_industry_id?: string;
  current_goal_id?: string;
}

export interface SearchResultItem {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  entity_type: SearchEntityType;
  score: number;
  reading_time: number | null;
  difficulty: string | null;
  confidence_score: number | null;
  industry_id: string | null;
  goal_id: string | null;
}

export interface SearchQueryRecord {
  query: string;
  session_id?: string;
  search_type: 'keyword' | 'semantic' | 'hybrid' | 'autocomplete';
  results_count: number;
  response_time_ms?: number;
  filters_used?: Record<string, unknown>;
  context_industry_id?: string;
  context_goal_id?: string;
}

export async function fullTextSearch(
  query: string,
  context?: SearchContext,
  filters?: SearchFilters,
  limit = 20
): Promise<SearchResultItem[]> {
  const { data, error } = await supabase.rpc('multi_entity_search', {
    search_query: query,
    context_industry_id: context?.current_industry_id || null,
    context_goal_id: context?.current_goal_id || null,
    result_limit: limit,
  });

  if (error) throw error;

  let results = (data ?? []) as SearchResultItem[];

  if (filters?.entity_type) {
    results = results.filter((r) => r.entity_type === filters.entity_type);
  }
  if (filters?.industry_id) {
    results = results.filter((r) => r.industry_id === filters.industry_id);
  }
  if (filters?.goal_id) {
    results = results.filter((r) => r.goal_id === filters.goal_id);
  }
  if (filters?.difficulty) {
    results = results.filter((r) => r.difficulty === filters.difficulty);
  }

  return results;
}

export async function generateQueryEmbedding(text: string): Promise<number[] | null> {
  try {
    const response = await fetch(
      `${env.supabaseUrl}/functions/v1/generate-embedding`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${env.supabaseAnonKey}`,
        },
        body: JSON.stringify({ text, store: false }),
      }
    );

    if (!response.ok) return null;

    const data = await response.json();
    return data.embedding ?? null;
  } catch {
    return null;
  }
}

export async function hybridSearch(
  query: string,
  queryEmbedding: number[] | null,
  filters?: SearchFilters,
  limit = 20
): Promise<SearchResultItem[]> {
  const { data, error } = await supabase.rpc('hybrid_search', {
    search_query: query,
    query_embedding: queryEmbedding ? JSON.stringify(queryEmbedding) : null,
    filter_industry_id: filters?.industry_id || null,
    filter_goal_id: filters?.goal_id || null,
    filter_difficulty: filters?.difficulty || null,
    result_limit: limit,
  });

  if (error) throw error;

  return (data ?? []).map((row: Record<string, unknown>) => ({
    id: row.id as string,
    slug: row.slug as string,
    title: row.title as string,
    summary: row.summary as string | null,
    entity_type: row.entity_type as SearchEntityType,
    score: (row.combined_score as number) || 0,
    reading_time: row.reading_time as number | null,
    difficulty: row.difficulty as string | null,
    confidence_score: row.confidence_score as number | null,
    industry_id: row.industry_id as string | null,
    goal_id: row.goal_id as string | null,
  }));
}

export async function autocomplete(
  prefix: string,
  limit = 8
): Promise<SearchResultItem[]> {
  const pattern = `%${prefix}%`;

  const knowledgeQuery = supabase
    .from('knowledge_objects')
    .select('id, slug, title, summary, reading_time, difficulty, confidence_score, industry_id, goal_id')
    .eq('status', 'published')
    .ilike('title', pattern)
    .limit(3);

  const industriesQuery = supabase
    .from('industries')
    .select('id, slug, name, description')
    .eq('is_active', true)
    .ilike('name', pattern)
    .limit(2);

  const goalsQuery = supabase
    .from('business_goals')
    .select('id, slug, title, description')
    .eq('is_active', true)
    .ilike('title', pattern)
    .limit(2);

  const workflowsQuery = supabase
    .from('workflows')
    .select('id, slug, title, overview, difficulty')
    .eq('status', 'published')
    .ilike('title', pattern)
    .limit(2);

  const productsQuery = supabase
    .from('products')
    .select('id, slug, title, description')
    .eq('is_active', true)
    .ilike('title', pattern)
    .limit(2);

  const [knowledge, industries, goals, workflows, products] = await Promise.all([
    knowledgeQuery, industriesQuery, goalsQuery, workflowsQuery, productsQuery,
  ]);

  const results: SearchResultItem[] = [];

  (knowledge.data ?? []).forEach((ko) => {
    results.push({
      id: ko.id, slug: ko.slug, title: ko.title, summary: ko.summary,
      entity_type: 'knowledge', score: 1, reading_time: ko.reading_time,
      difficulty: ko.difficulty, confidence_score: ko.confidence_score,
      industry_id: ko.industry_id, goal_id: ko.goal_id,
    });
  });

  (industries.data ?? []).forEach((i) => {
    results.push({
      id: i.id, slug: i.slug, title: i.name, summary: i.description,
      entity_type: 'industry', score: 1, reading_time: null,
      difficulty: null, confidence_score: null, industry_id: i.id, goal_id: null,
    });
  });

  (goals.data ?? []).forEach((g) => {
    results.push({
      id: g.id, slug: g.slug, title: g.title, summary: g.description,
      entity_type: 'goal', score: 1, reading_time: null,
      difficulty: null, confidence_score: null, industry_id: null, goal_id: g.id,
    });
  });

  (workflows.data ?? []).forEach((w) => {
    results.push({
      id: w.id, slug: w.slug, title: w.title, summary: w.overview,
      entity_type: 'workflow', score: 1, reading_time: null,
      difficulty: w.difficulty, confidence_score: null, industry_id: null, goal_id: null,
    });
  });

  (products.data ?? []).forEach((p) => {
    results.push({
      id: p.id, slug: p.slug, title: p.title, summary: p.description,
      entity_type: 'product', score: 1, reading_time: null,
      difficulty: null, confidence_score: null, industry_id: null, goal_id: null,
    });
  });

  return results.slice(0, limit);
}

export async function getPopularSearches(limit = 10): Promise<string[]> {
  const { data, error } = await supabase
    .from('search_queries')
    .select('query')
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) return [];

  const counts = new Map<string, number>();
  (data ?? []).forEach((row) => {
    const q = (row.query as string).toLowerCase().trim();
    if (q.length >= 2) counts.set(q, (counts.get(q) || 0) + 1);
  });

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([q]) => q);
}

export async function getTrendingTopics(): Promise<SearchResultItem[]> {
  const { data, error } = await supabase
    .from('knowledge_objects')
    .select('id, slug, title, summary, reading_time, difficulty, confidence_score, industry_id, goal_id')
    .eq('status', 'published')
    .order('published_at', { ascending: false })
    .limit(6);

  if (error) return [];

  return (data ?? []).map((ko) => ({
    id: ko.id, slug: ko.slug, title: ko.title, summary: ko.summary,
    entity_type: 'knowledge' as SearchEntityType, score: 1,
    reading_time: ko.reading_time, difficulty: ko.difficulty,
    confidence_score: ko.confidence_score, industry_id: ko.industry_id,
    goal_id: ko.goal_id,
  }));
}

export async function recordSearchQuery(record: SearchQueryRecord): Promise<string | null> {
  const { data, error } = await supabase
    .from('search_queries')
    .insert({
      query: record.query,
      session_id: record.session_id || crypto.randomUUID(),
      search_type: record.search_type,
      results_count: record.results_count,
      response_time_ms: record.response_time_ms,
      filters_used: record.filters_used || {},
      context_industry_id: record.context_industry_id || null,
      context_goal_id: record.context_goal_id || null,
      embeddings_used: record.search_type === 'hybrid' || record.search_type === 'semantic',
    })
    .select('id')
    .single();

  if (error) return null;
  return data?.id ?? null;
}

export async function recordSearchClick(searchQueryId: string, resultId: string): Promise<void> {
  await supabase
    .from('search_queries')
    .update({ clicked_result_id: resultId })
    .eq('id', searchQueryId);
}

export async function reportKnowledgeGap(query: string, userId?: string): Promise<void> {
  await supabase.rpc('upsert_knowledge_gap', {
    p_question: query,
    p_user_id: userId || null,
  });
}

export async function getFallbackResults(): Promise<{
  problems: SearchResultItem[];
  industries: SearchResultItem[];
  goals: SearchResultItem[];
  workflows: SearchResultItem[];
}> {
  const [problemsRes, industriesRes, goalsRes, workflowsRes] = await Promise.all([
    supabase
      .from('business_problems')
      .select('id, slug, title, short_description, industry_id')
      .eq('status', 'published')
      .limit(4),
    supabase
      .from('industries')
      .select('id, slug, name, description')
      .eq('is_active', true)
      .limit(4),
    supabase
      .from('business_goals')
      .select('id, slug, title, description')
      .eq('is_active', true)
      .limit(4),
    supabase
      .from('workflows')
      .select('id, slug, title, overview, difficulty')
      .eq('status', 'published')
      .limit(4),
  ]);

  return {
    problems: (problemsRes.data ?? []).map((p) => ({
      id: p.id, slug: p.slug, title: p.title, summary: p.short_description,
      entity_type: 'problem' as SearchEntityType, score: 0,
      reading_time: null, difficulty: null, confidence_score: null,
      industry_id: p.industry_id, goal_id: null,
    })),
    industries: (industriesRes.data ?? []).map((i) => ({
      id: i.id, slug: i.slug, title: i.name, summary: i.description,
      entity_type: 'industry' as SearchEntityType, score: 0,
      reading_time: null, difficulty: null, confidence_score: null,
      industry_id: i.id, goal_id: null,
    })),
    goals: (goalsRes.data ?? []).map((g) => ({
      id: g.id, slug: g.slug, title: g.title, summary: g.description,
      entity_type: 'goal' as SearchEntityType, score: 0,
      reading_time: null, difficulty: null, confidence_score: null,
      industry_id: null, goal_id: g.id,
    })),
    workflows: (workflowsRes.data ?? []).map((w) => ({
      id: w.id, slug: w.slug, title: w.title, summary: w.overview,
      entity_type: 'workflow' as SearchEntityType, score: 0,
      reading_time: null, difficulty: w.difficulty, confidence_score: null,
      industry_id: null, goal_id: null,
    })),
  };
}
